import * as React from 'react';
import injectSheet from 'react-jss';
import moment from 'moment';
import { Tooltip, Icon } from 'antd';
import AnchorIcon from 'src/components/common/icons/AnchorIcon';
import ShipFilled from 'src/components/common/icons/ShipFilled';
import { connect } from 'react-redux';
import { containerTrackingPermissions } from 'src/selectors/importsFilter';
import { CTPermissions } from './Interfaces/TypesInShipmentTracking';
import { CardData } from './Interfaces/Shipment.interface';
import { stops, isValidArray } from 'src/Library/Helper';
import { StylesProps } from 'src/Library/Types';
import { SHIPMENT_TYPE } from './ContainerUtils';
import { tokens } from 'src/design-system';
import AirplaneIcon from 'src/components/common/icons/AirplaneIcon';

const COLORS = {
  GREEN: '#44D13D',
  GREY: '#999999',
};

const styles = (theme: any) => ({
  anchor: {
    color: tokens.colors.primary,
    fontSize: 11,
    fontWeight: tokens.fontWeight.semibold,
    cursor: 'pointer',
  },
  flexCol: {
    display: 'flex',
    flexDirection: 'column',
  },
  dot: {
    height: '12px',
    width: '12px',
    borderRadius: '6px',
  },
  dotContent: {
    maxWidth: 80,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    position: 'absolute',
    bottom: '-18px',
    fontSize: '0.8vw',
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: '17px',
  },
  line: {
    height: '0',
    display: 'flex',
    flex: '1',
    marginRight: '3px',
  },
  emptySpan: {
    height: '8px',
    width: '8px',
    borderRadius: tokens.radius.md,
  },
  dotForDetailsPage: {
    height: '6px',
    width: '6px',
    borderRadius: '3px',
  },
  noTracking: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: '10px',
    paddingRight: '30px',
    color: tokens.colors.textSecondary,
    height: '40px',
    lineHeight: '20px',
  },
  anchorIcon: {
    position: 'absolute',
    bottom: '-20px',
    marginLeft: '-3px',
  },
  trackingContent: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
  },
  withAnchor: {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
  },
  question: {
    position: 'absolute',
    bottom: '-20px',
    fontSize: tokens.fontSize.small,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: '17px',
    maxWidth: '100px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  trackingErrorMessage: {
    marginTop: '8%',
    color: tokens.colors.error,
    fontSize: tokens.fontSize.small,
  },
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, ReduxProps {
  openEditForm: () => void;
  openEditFormNew: () => void;
  isArchived: boolean;
  cardData: CardData;
  tabId: string;
  isTrackingFailed: boolean;
  isDetailPage?: boolean;
  isAWB?: boolean;
}

interface ReduxProps {
  permissions: CTPermissions;
  newShipmentView?: boolean,
}

const TrackingShipView = (props: IProps) => {

  const {
    openEditForm,
    newShipmentView,
    openEditFormNew,
    permissions,
    cardData,
    classes,
    tabId,
    isTrackingFailed,
    isDetailPage,
    isAWB,
  } = props;

  const renderDot = (color, name, isOrigin, key) => {
    return <span className={classes.flexCol} key={key}>
      <span
        style={{ backgroundColor: color }}
        className={isDetailPage ? classes.dotForDetailsPage : classes.dot}
      />
      {!isDetailPage && <span
        className={classes.dotContent}
        style={{ [isOrigin ? 'left' : 'right']: isOrigin ? '-10px' : '0px' }}>
        <Tooltip
          placement="topLeft"
          title={name}>
          {name}
        </Tooltip>
      </span>}
    </span>;
  };

  const renderQuestionMark = (color, name, isOrigin, key) => {
    return <span
      className={classes.flexCol}
      key={key}>
      <Icon
        type="question-circle"
        style={{ color, fontSize: '15px' }}
      />
      <span
        className={classes.dotContent}
        style={{  [isOrigin ? 'left' : 'right']: isOrigin ? '10px' : '43px' }}>
        {name}
      </span>
    </span>;
  };

  const renderLine = (color, key) => {
    return <span
      key={key}
      className={classes.line}
      style={{  borderTop: `1px solid ${color}` }}
    />;
  };

  const renderTitle = (lastLocation) => {
    let lastEvent = '';
    let lastAddress = '0';
    let lastTime = '';
    if (lastLocation?.event && lastLocation.event[0]) {
      lastEvent = lastLocation.event[0];
    }
    if (lastLocation?.location) {
      if (lastEvent !== '') {
        lastAddress = `at ${lastLocation.location}`;
      }
      if (lastLocation?.timestamp) {
        lastTime = `on ${moment(lastLocation.timestamp).utc().format('DD/MM/YYYY')}`;
      }
    }
    return `${lastEvent} ${lastAddress} ${lastTime}`;
  };

  const renderLastLocation = (lastLocation) => {
    if (lastLocation) {
      return <Tooltip
        title={renderTitle(lastLocation)}>
        {renderShipFilled()}
      </Tooltip>;
    }
    return renderShipFilled();
  };

  const renderShipFilled = () => {
    if (!isAWB) {
      return <ShipFilled
        height={isDetailPage ? '12px' : '19px'}
        width={isDetailPage ? '18px' : '30px'}
        color={cardData.isOnHold ? '#DFC12F' : undefined}
      />;
    }
    return <AirplaneIcon height="18px" width="18px" color={COLORS.GREEN} />;
  };

  const renderPorts = (withAnchor, renderQuestion, event, key, isOrigin, portName) => {
    if (withAnchor) {
      return <span
        className={classes.withAnchor}
        style={{ width: isDetailPage ? '15px' : '30px', bottom: isDetailPage ? '-10px' : '-20px' }}>
        <Tooltip
          key={key}
          title={event?.location || null}>
          {isDetailPage ? <AnchorIcon height={'9px'} width={'7px'}/> : <AnchorIcon/>}
        </Tooltip>
      </span>;
    }
    if (renderQuestion) {
      return <span
        className={classes.question}
        style={{
          maxWidth: '70px',
          [isOrigin ? 'left' : 'right']: isOrigin ? '10px' : '43px',
        }}>
        {renderAddPort(renderQuestion)}
      </span>;
    }
    return <span
      style={{
        maxWidth: '100px',
        [isOrigin ? 'left' : 'right']: isOrigin ? '10px' : '43px',
      }}
      className={classes.question}>
      <Tooltip
        placement="topLeft"
        title={portName || ''}>
        {portName || ''}
      </Tooltip>
    </span>;
  };

  const renderShipIcon = (
    withAnchor,
    key,
    portName = '',
    isOrigin = false,
    event: any = null,
    renderQuestion: any = false,
  ) => {
    const lastLocation = cardData.lastTrackingEvent || null;
    return <span
      className={classes.flexCol}
      key={`span-${key}`}>
      {renderLastLocation(lastLocation)}
      {renderPorts(withAnchor, renderQuestion, event, key, isOrigin, portName)}
    </span>;
  };

  const renderAnchor = (color, key, event) => {
    return <Tooltip
      title={event.location}
      key={key}>
      <span className={classes.flexCol}>
        <span
          style={{ backgroundColor: color }}
          className={isDetailPage ? classes.dotForDetailsPage : classes.emptySpan}
        />
        <span
          className={classes.anchorIcon}>
          <AnchorIcon/>
        </span>
    </span>
    </Tooltip>;
  };

  function originPortName() {
    return cardData.originPortName || cardData.loadingPortName || cardData.containerOriginPortName || cardData.containerLoadingPortName;
  }

  function destinationPortName() {
    return cardData.destinationPortName || cardData.dischargePortName || cardData.containerDestinationPortName || cardData.containerDischargePortName;
  }

  function isTrackingAvailable() {
    if (isManuallyTracked()) return true;
    return (
      isValidArray(cardData.tracking) ||
      originPortName() ||
      destinationPortName()
    );
  }

  const renderNoTracking = () => {
    if (
      cardData.infoFetchFailureMessage ||
      cardData.trackingFetchFailureMessage ||
      !cardData.shippingLineId ||
      isTrackingFailed
    ) return null;
    if (!cardData.bookingNumber && !cardData.blNumber && !cardData.containerNumber) return null;
    return <div
      className={classes.noTracking}>
      <Icon
        type="exclamation-circle"
        theme="filled"
        style={{ marginRight: '5px', color: '#FA8C16' }}
      />
      Tracking details not yet provided by {cardData.shippingLineName}
    </div>;
  };

  function getOriginLocation(location) {
    if (originPortName()) return originPortName();
    if (isManuallyTracked()) return renderAddPort('Add Origin');
    return location;
  }

  function handleAtOrigin(arr, trackingMap) {
    if (trackingMap.AT_ORIGIN) {
      arr.push({
        ...trackingMap.AT_ORIGIN,
        location: getOriginLocation(trackingMap.AT_ORIGIN.location),
      });
    } else {
      arr.push({
        ...getOrigin(),
        end: -1,
      });
    }
  }

  function getOrigin() {
    return {
      statusInternal: 'AT_ORIGIN',
      isCurrentEvent: false,
      location: getOriginLocation('N/A'),
      end: 0,
    };
  }

  function getDestinationLocation(location) {
    if (isDetailPage) return null;
    if (destinationPortName()) return destinationPortName();
    if (isManuallyTracked()) return renderAddPort('Add Destination');
    return location;
  }

  function handleReachedDestination(arr, trackingMap) {
    if (trackingMap.REACHED_DESTINATION_PORT) {
      arr.push({
        ...trackingMap.REACHED_DESTINATION_PORT,
        location: getDestinationLocation(trackingMap.REACHED_DESTINATION_PORT.location),
      });
    } else {
      handleDestination(arr);
    }
  }

  function handleDestination(arr) {
    arr.push({
      statusInternal: 'REACHED_DESTINATION_PORT',
      isCurrentEvent: false,
      location: getDestinationLocation('N/A'),
      start: cardData.tracking ? (cardData.tracking.length || 0) : -1,
    });
  }

  function handleOrigin(event, newArr, index, trackingMap) {
    if (event.isCurrentEvent) {
      const renderQuestion = isManuallyTracked() && !originPortName();
      newArr.push(getShipIcon('Add Origin', event, index, renderQuestion));
      newArr.push(renderLine(COLORS.GREY, `line-${index}`));
      trackingMap.currentEvent = true;
    } else {
      const color = getColor(event);
      const renderQuestion = isManuallyTracked() && !originPortName();
      newArr.push(
        renderQuestion ? renderQuestionMark(color, event.location, true, `question-${index}`) :
        renderDot(color, event.location, true, `dot-${index}`),
      );
      newArr.push(renderLine(color, `line-${index}`));
    }
  }

  function getColor(event) {
    if (event.isNoTrackingEvent) return COLORS.GREY;
    if (cardData.currentEventIndex > event.end) return COLORS.GREEN;
    return COLORS.GREY;
  }

  function getShipIcon(location: string, event, index: number, set, isOrigin = true) {
    return renderShipIcon(
      false,
      `ship-${index}`,
      event.location,
      isOrigin,
      null,
      set ? location : null,
    );
  }

  function handleEventAtDestination(event, newArr, index, trackingMap, arr) {
    if (event.isCurrentEvent) {
      const renderQuestion = isManuallyTracked() && !destinationPortName();
      newArr.push(getShipIcon('Add Destination', event, index, renderQuestion, false));
      trackingMap.currentEvent = true;
    } else {
      if (
        cardData.currentEventIndex < event.start &&
        cardData.currentEventIndex > ((arr[index - 1] || {}).end || 0) &&
       !trackingMap.currentEvent
      ) {
        newArr.push(renderShipIcon(false, `ship-${index}`));
        newArr.push(renderLine(COLORS.GREY, `line-${index}`));
        trackingMap.currentEvent = true;
      }
      const renderQuestion = isManuallyTracked() && !destinationPortName();
      newArr.push(
        renderQuestion ?
        renderQuestionMark(COLORS.GREY, event.location, false, `question-${index}`) :
        renderDot(COLORS.GREY, event.location, false, `dot-${index}`),
      );
    }
  }

  function handlePushOrigin(trackingMap, data, idx) {
    const isCurrentEvent = idx === cardData.currentEventIndex;
    if (!trackingMap[data.statusInternal]) {
      trackingMap[data.statusInternal] = {
        isCurrentEvent,
      };
    } else {
      trackingMap[data.statusInternal] = {
        isCurrentEvent: trackingMap[data.statusInternal].isCurrentEvent || isCurrentEvent,
      };
    }
    trackingMap[data.statusInternal] = {
      ...trackingMap[data.statusInternal],
      ...data,
      end: idx,
    };
  }

  function handlePushDestination(trackingMap, data, idx) {
    if (!trackingMap[data.statusInternal]) {
      trackingMap[data.statusInternal] = {
        isCurrentEvent: idx <= cardData.currentEventIndex,
        start: idx,
        ...data,
      };
    }
  }

  function handlePushTransshipment(trackingMap, data, arr, idx) {
    if (!trackingMap[data.location]) {
      arr.push({
        isCurrentEvent: idx === cardData.currentEventIndex,
        start: idx,
        end: idx,
        ...data,
      });
      trackingMap[data.location] = true;
    } else {
      arr[arr.length - 1] = {
        start: arr[arr.length - 1].start,
        end: idx,
        ...data,
      };
    }
  }
  const renderShip = () => {
    if (!isTrackingAvailable()) return renderNoTracking();
    let arr: any [] = [];
    const trackingMap = {};
    let filteredTransshipments: any[] = [];
    if (isValidArray(cardData.tracking)) {
      for (let i = 0; i < cardData.tracking.length; i += 1) {
        const data = cardData.tracking[i];
        switch (data.statusInternal) {
          case 'AT_ORIGIN': {
            handlePushOrigin(trackingMap, data, i);
            break;
          }
          case 'REACHED_DESTINATION_PORT': {
            handlePushDestination(trackingMap, data, i);
            break;
          }
          case 'TRANSSHIPMENT': {
            handlePushTransshipment(trackingMap, data, arr, i);
            break;
          }
          default: break;
        }
      }
      if (arr.length > 0) {
        if (arr[arr.length - 1].isPastEvent || arr.length <= 2) {
          filteredTransshipments = arr.slice(arr.length - 2);
        } else if (!arr[0].isPastEvent) {
          filteredTransshipments = arr.slice(0, 2);
        } else {
          for (let i = arr.length - 2; i >= 0; i -= 1) {
            if (arr[i].isPastEvent) {
              filteredTransshipments = arr.slice(i, i + 2);
              break;
            }
          }
        }
      }
      arr = [];
      handleAtOrigin(arr, trackingMap);
      arr = arr.concat(filteredTransshipments);
      handleReachedDestination(arr, trackingMap);
    } else if (originPortName() || destinationPortName()) {
      arr.push({
        ...getOrigin(),
        isNoTrackingEvent: true,
      });
      handleDestination(arr);
    } else if (isManuallyTracked()) {
      arr.push({
        ...getOrigin(),
        isNoTrackingEvent: true,
        location: originPortName() || renderAddPort('Add Origin'),
      });
      arr.push({
        statusInternal: 'REACHED_DESTINATION_PORT',
        isCurrentEvent: false,
        location: destinationPortName() || renderAddPort('Add Destination'),
        start: -1,
      });
    } else if (cardData.hasTracking) {
      arr.push({
        ...getOrigin(),
        isNoTrackingEvent: true,
        location: renderAddPort('Add Origin'),
      });
      arr.push({
        statusInternal: 'REACHED_DESTINATION_PORT',
        isCurrentEvent: false,
        location: renderAddPort('Add Destination'),
        start: -1,
      });
    }
    return <div
      className={classes.trackingContent}>
      {renderShipHelper(arr)}
    </div>;
  };

  const renderShipHelper = (arr) => {
    const newArr: any[] = [];
    const trackingMap = {};
    arr.forEach((event, index) => {
      switch (event.statusInternal) {
        case 'AT_ORIGIN': {
          handleOrigin(event, newArr, index, trackingMap);
          break;
        }
        case 'REACHED_DESTINATION_PORT': {
          handleEventAtDestination(event, newArr, index, trackingMap, arr);
          break;
        }
        case 'TRANSSHIPMENT': {
          handleTransshipment(newArr, event, index, trackingMap);
          break;
        }
      }
    });
    return newArr;
  };

  function handleTransshipment(newArr, event, index, trackingMap) {
    if (cardData.currentEventIndex >= event.start && cardData.currentEventIndex <= event.end) {
      newArr.push(renderShipIcon(true, `ship-${index}`, '', false, event));
      newArr.push(renderLine(COLORS.GREY, `line-${index}`));
      trackingMap.currentEvent = true;
    } else {
      if (cardData.currentEventIndex < event.start && !trackingMap.currentEvent) {
        newArr.push(renderShipIcon(false, `ship-${index}`));
        newArr.push(renderLine(COLORS.GREY, `line-${index}`));
        trackingMap.currentEvent = true;
      }
      newArr.push(renderAnchor(trackingMap.currentEvent ?
      COLORS.GREY : COLORS.GREEN, `anchor-${index}`, event));
      newArr.push(renderLine(trackingMap.currentEvent ?
      COLORS.GREY : COLORS.GREEN, `line-end-${index}`));
    }
  }

  const renderAddPort = (text) => {
    const isArchived = cardData.isArchived;
    if (!permissions.write) return 'N/A';
    return <span
      className={classes.anchor}
      onClick={(e) => {
        stops(e);
        if (isArchived) return;
        if (newShipmentView) openEditFormNew();
        else openEditForm();
      }}>
      {text}
    </span>;
  };

  function isManuallyTracked() {
    return !!(cardData.isManuallyTracked);
  }

  return (
    <div>
      {renderShip()}
      {(cardData.trackingFetchFailureMessage && moment().diff(moment(cardData.lastSuccessfulTrackingStatusUpdatedAt), 'hours') >= 48 && !cardData.isArchived) ?
      <div className={classes.trackingErrorMessage}>
        <Icon type="info-circle" theme="filled" style={{ color: tokens.colors.error, marginRight: '7px' }} />
        {cardData.trackingFetchFailureMessage}
      </div> : <></>}
    </div>
  );
};

const mapStateToProps = ({ masterData }) => ({
  permissions: containerTrackingPermissions(masterData),
  newShipmentView: masterData.newShipmentView,
});

export default injectSheet(styles)(connect(mapStateToProps)(TrackingShipView));
